import { scroll } from 'motion'
import { BREAKPOINTS } from '@/lib/breakpoints'

const STEP_SPACING_PX = 64
const SVG_NS = 'http://www.w3.org/2000/svg'

// Reduced-motion visitors get the full trail statically; everyone else watches it walk in.
export function mountLandingTrail(trail: HTMLElement): () => void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const media = window.matchMedia(BREAKPOINTS.medium)
  const geometry = document.createElementNS(SVG_NS, 'path')
  const holder = document.createElementNS(SVG_NS, 'svg')
  holder.setAttribute('class', 'home-trail__geometry')
  holder.setAttribute('aria-hidden', 'true')
  holder.append(geometry)
  let steps: HTMLElement[] = []

  const toPx = (length: number, width: number, height: number) => {
    const point = geometry.getPointAtLength(length)
    return { x: (point.x / 100) * width, y: (point.y / 100) * height }
  }

  const build = () => {
    const compact = media.matches
    geometry.setAttribute(
      'd',
      (compact ? trail.dataset.trailMobile : undefined) ?? trail.dataset.trail ?? '',
    )
    trail.replaceChildren(holder)
    steps = []
    const { width, height } = trail.getBoundingClientRect()
    if (!width || !height) return

    const total = geometry.getTotalLength()
    if (!total) return
    let lengthPx = 0
    let previous = toPx(0, width, height)
    for (let i = 1; i <= 24; i++) {
      const point = toPx((total * i) / 24, width, height)
      lengthPx += Math.hypot(point.x - previous.x, point.y - previous.y)
      previous = point
    }

    const count = Math.max(6, Math.min(42, Math.round(lengthPx / STEP_SPACING_PX)))
    for (let i = 0; i < count; i++) {
      const at = (total * (i + 0.5)) / count
      const point = toPx(at, width, height)
      const ahead = toPx(Math.min(total, at + total / 100), width, height)
      const angle = Math.atan2(ahead.y - point.y, ahead.x - point.x)
      const side = (i % 2 === 0 ? -1 : 1) * (compact ? 4 : 7)
      const step = document.createElement('div')
      step.className = 'home-step'
      step.style.setProperty(
        '--sx',
        `${(point.x + Math.cos(angle + Math.PI / 2) * side).toFixed(1)}px`,
      )
      step.style.setProperty(
        '--sy',
        `${(point.y + Math.sin(angle + Math.PI / 2) * side).toFixed(1)}px`,
      )
      step.style.setProperty('--sr', `${((angle * 180) / Math.PI).toFixed(1)}deg`)
      if (reduced) step.dataset.on = ''
      trail.append(step)
      steps.push(step)
    }
  }

  build()
  const observer = new ResizeObserver(build)
  observer.observe(trail)
  media.addEventListener('change', build)
  const cancels: Array<VoidFunction> = [
    () => {
      observer.disconnect()
      media.removeEventListener('change', build)
    },
  ]

  if (!reduced) {
    cancels.push(
      scroll(
        (progress: number) => {
          const lit = Math.round(progress * steps.length)
          steps.forEach((step, i) => {
            if (i < lit) step.dataset.on = ''
            else delete step.dataset.on
          })
        },
        { target: trail, offset: ['start 0.9', 'end 0.5'] },
      ),
    )
  }

  return () => {
    for (const cancel of cancels) cancel()
  }
}
